import { trade } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import { getClient, getLeagueIdForLid, getLobbyState } from "./index.ts";
import { buildTradeEvent } from "./trades.ts";
import { syncNow, type MpTradeEventPayload } from "./sync.ts";

export type MpTradeOffer = {
	id: string;
	league_id: string;
	from_user: string;
	from_tid: number;
	to_tid: number;
	payload: MpTradeEventPayload;
	status: "pending" | "accepted" | "rejected" | "invalid" | "cancelled";
	created_at: string;
	resolved_at: string | null;
};

// Send the trade currently built on the Trade screen as an offer to the human
// controlling the other team.
export const sendOfferFromCurrentTrade = async (): Promise<{
	status: "sent" | "not_multiplayer" | "not_human";
}> => {
	const lid = g.get("lid");
	const leagueId =
		typeof lid === "number" ? await getLeagueIdForLid(lid) : undefined;
	if (!leagueId) {
		return { status: "not_multiplayer" };
	}

	const { teams } = await trade.get();
	if (
		teams[0].pids.length +
			teams[0].dpids.length +
			teams[1].pids.length +
			teams[1].dpids.length ===
		0
	) {
		throw new Error("The trade is empty - add some players or picks first");
	}

	const lobby = await getLobbyState(leagueId);
	const otherHuman = lobby.members.find((m) => m.tid === teams[1].tid);
	if (!otherHuman) {
		return { status: "not_human" };
	}

	const { event } = await buildTradeEvent(teams as any);

	const { error } = await getClient().rpc("create_trade_offer", {
		p_league_id: leagueId,
		p_to_tid: teams[1].tid,
		p_payload: event,
	});
	if (error) {
		throw new Error(error.message);
	}

	await trade.clear();
	return { status: "sent" };
};

export const listOffers = async (leagueId: string): Promise<MpTradeOffer[]> => {
	const { data, error } = await getClient()
		.from("trade_offers")
		.select()
		.eq("league_id", leagueId)
		.order("created_at", { ascending: false })
		.limit(50);
	if (error) {
		throw new Error(error.message);
	}
	return (data ?? []) as MpTradeOffer[];
};

export const respondOffer = async (
	offerId: string,
	accept: boolean,
): Promise<{
	status: string;
	message?: string;
}> => {
	const { data, error } = await getClient().rpc("respond_trade_offer", {
		p_offer_id: offerId,
		p_accept: accept,
	});
	if (error) {
		throw new Error(error.message);
	}

	const result = data as {
		status: string;
		kind?: string;
		id?: number;
		tid?: number;
		used?: number;
		budget?: number;
		checkpoint?: string;
	};

	if (result.status === "budget_exceeded") {
		const lid = g.get("lid");
		const leagueId =
			typeof lid === "number" ? await getLeagueIdForLid(lid) : undefined;
		let who = "One of the teams has";
		if (leagueId) {
			const lobby = await getLobbyState(leagueId);
			const session = await getClient().auth.getSession();
			const me = lobby.members.find(
				(m) => m.user_id === session.data.session?.user.id,
			);
			who = result.tid === me?.tid ? "You have" : "The other team has";
		}
		return {
			status: "invalid",
			message: `${who} used all ${result.budget} trades allowed during the ${result.checkpoint} checkpoint, so this offer can't be accepted right now. Budgets reset when the league advances.`,
		};
	}

	if (result.status === "accepted") {
		const lid = g.get("lid");
		const leagueId =
			typeof lid === "number" ? await getLeagueIdForLid(lid) : undefined;
		if (leagueId) {
			await syncNow(leagueId);
		}
		return { status: "accepted" };
	}

	if (result.status === "conflict") {
		let message =
			"This offer is no longer valid: an asset in it changed hands since it was sent.";
		if (result.kind === "player" && result.id !== undefined) {
			const p = await idb.cache.players.get(result.id);
			if (p) {
				message = `This offer is no longer valid: ${p.firstName} ${p.lastName} changed teams since it was sent.`;
			}
		}
		return { status: "invalid", message };
	}

	return { status: result.status };
};

export const cancelOffer = async (offerId: string) => {
	const { error } = await getClient().rpc("cancel_trade_offer", {
		p_offer_id: offerId,
	});
	if (error) {
		throw new Error(error.message);
	}
};
